import React, { useEffect, useCallback, useContext } from 'react';
import {
  number,
  oneOfType,
  arrayOf,
  string,
  func,
  bool
} from 'prop-types';
import {
  Alert,
  Button,
  FormController,
  FormLabel,
  Radio, RadioGroup, SelectionControlLabel,
  Typography
} from '@one-thd/sui-atomic-components';
import { Calculator as CalculatorIcon } from '@one-thd/sui-icons';
import { alias, shape as shapeType, string as stringType } from '@thd-nucleus/data-sources';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { track } from './subcomponents/Analytics/analytics';
import { getDefaultArea, useCalculator } from '../hooks/useCalculator';
import { MulchOptions } from './subcomponents/MulchOptions/MulchOptions';
import { saveStorageObject } from '../localStorageUtil';
import { CalculatorModal } from './subcomponents/CalculatorModal/CalculatorModal';
import { CalculatorInputElement } from './subcomponents/CalculatorInputElement';
import { useCalculatorLocalStorage } from '../hooks/useCalculatorLocalStorage';

const Calculator = ({
  unitsPerCase,
  addAdditional,
  calculatorType,
  maxAreaNumber,
  lineItemName,
  dimensionNames,
  productCategory,
  onCalculate,
  hideHeader,
  hideMeasuringTips,
  showMulchOptions,
  showDescriptions,
  hideAreaText,
  descriptionText,
  itemId
}) => {
  const MIN_AREA_NUMBER = 1;
  const {
    channel
  } = useContext(ExperienceContext);
  const [calculate,
    { setCalculate, addElement, calculateTotal }
  ] = useCalculator({
    showMulchOptions, unitsPerCase, addAdditional, calculatorType, lineItemName
  });
  const isLocalStorageEnabled = useConfigService('fs:isLocalStorageEnabled');

  const { storageKey, storageObj } = useCalculatorLocalStorage({ itemId, isLocalStorageEnabled });

  const bindCalcTotal = useCallback(({ isClick }) => {
    const result = calculateTotal({ isClick }) || {};
    if (onCalculate) {
      onCalculate({
        mulchType: result.mulchType,
        bagSize: result.bagSize,
        quantity: result.quantity,
        bulkQuantity: result.bulkQuantity,
        coverage: result.coverage,
        error: result.hasError
      });
    }
  }, [calculate, onCalculate, calculateTotal]);

  useEffect(() => {
    if (isLocalStorageEnabled && storageObj) {
      setCalculate({
        ...calculate,
        ...storageObj,
        isFromLocalStorage: true
      });
    }
  }, [storageObj === null]);

  useEffect(() => {
    if (calculate.isFromLocalStorage) {
      bindCalcTotal({ click: false });
    }
  }, [calculate.isFromLocalStorage]);

  useEffect(() => {
    if (isLocalStorageEnabled && storageKey) {
      saveStorageObject(storageKey, calculate);
    }
  }, [calculate]);

  useEffect(() => {
    if (calculate.isClicked) {
      track();
    }
  }, [calculate.isClicked]);

  const handleBagSize = useCallback((event) => {
    const { value = '' } = event.target;
    setCalculate({
      ...calculate,
      bagSize: value
    });
  }, [calculate]);

  const handleMulchType = useCallback((event) => {
    let mulchType = '';
    let bagSize = '';
    const { value = '' } = event.target;

    if (value === 'wood' || value === 'top soil') {
      mulchType = value;
      bagSize = '1.5';
    } else {
      mulchType = value;
      bagSize = '0.8';
    }
    setCalculate({
      ...calculate,
      mulchType,
      bagSize
    });
  }, [calculate]);

  // TODO: Use objects / refs instead of arrays to keep track of these values. useReducer
  const handleInputChange = useCallback((even, index, height, inputType) => {
    const { areas } = calculate;
    const re = /^[0-9\b]{0,5}$/;
    const { value = '', name = '' } = even.target;
    if (value === '' || re.test(value) || name === 'description') {
      if (name === 'depth') {
        areas[index].depth = value;
      } else if (name === 'description') {
        areas[index].name = value;
      } else if (inputType === 'lengthWidth') {
        if (height) {
          areas[index].length = value;
        } else {
          areas[index].width = value;
        }
      } else {
        areas[index].squareFootage = { value, changed: true };
      }
      setCalculate({
        ...calculate,
        isClicked: false,
        value,
        areas
      });
    }
  }, [calculate]);

  const removeInputElement = useCallback((index) => {
    const { step } = calculate;
    if (step > MIN_AREA_NUMBER) {
      setCalculate((prevState) => {
        const {
          mulchType,
          areas
        } = prevState;
        areas.splice(index, 1);

        return {
          ...calculate,
          step: areas.length,
          mulchType,
          areas
        };
      });
    }
  }, [calculate]);

  const handleToggleText = useCallback(() => {
    setCalculate((prevState) => {
      const {
        calcByArea,
        calcByLengthWidth,
        areas
      } = prevState;
      // Commented out dead code for future implementation
      // const valueSqFts = areas.map((area) => area.squareFootage.value)
      //   .filter((sqFt) => !!sqFt);
      // const valueLengths = areas.map((area) => area.length)
      //   .filter((sqFt) => !!sqFt);
      // const valueWidths = areas.map((area) => area.width)
      //   .filter((sqFt) => !!sqFt);
      // const areaFieldCount = valueSqFts.length;
      // const widthFieldCount = valueWidths.length;
      // const additionalRowsNeeded = calcByArea && areaFieldCount !== widthFieldCount
      //   ? areaFieldCount - widthFieldCount
      //   : 0;
      // // clear WxH when switching back from area if it's been changed
      // const updatedLengths = calcByArea
      //   ? valueLengths.map((height, heightIndex) => {
      //     return valueSqFts[heightIndex].changed ? '' : height;
      //   })
      //   : valueLengths;
      // const updatedWidths = calcByArea
      //   ? valueWidths.map((width, widthIndex) => {
      //     return valueSqFts[widthIndex].changed ? '' : width;
      //   })
      //   : valueWidths;
      // const newValueLengths = additionalRowsNeeded
      //   ? updatedLengths.concat(new Array(additionalRowsNeeded).fill(''))
      //   : updatedLengths;
      // const newValueWidths = additionalRowsNeeded
      //   ? updatedWidths.concat(new Array(additionalRowsNeeded).fill(''))
      //   : updatedWidths;
      // const newValueSqFts = calcByLengthWidth
      //   ? updatedLengths.map((width, widthIndex) => {
      //     if (!!updatedLengths[widthIndex] || !valueSqFts[widthIndex]) {
      //       return { value: `${width * (updatedLengths[widthIndex] || 0)}`, changed: false };
      //     }
      //     return valueSqFts[widthIndex];
      //   })
      //   : valueSqFts;
      const newToggle = calcByArea ? 'Square Footage' : `${dimensionNames.join(' x ')}`;
      return {
        ...calculate,
        calcByArea: !calcByArea,
        calcByLengthWidth: !calcByLengthWidth,
        hasError: false,
        step: 1,
        areas: [getDefaultArea({ index: 0, calculatorType, lineItemName })],
        toggleText: newToggle
      };
    });
  }, [calculate]);

  const prompt = 'How much will you need?';
  const description = 'Please note: calculations are estimates and can only be made using whole numbers.';
  const errorBox = {
    widthError: false,
    heightLengthError: false,
    areaError: false
  };
  const {
    areas, step
  } = calculate;

  return (
    <div className="sui-bg-transparent sui-mt-3 sui-mb-5" data-component="Calculator">
      {!hideHeader && (
        <div className="sui-flex">
          <CalculatorIcon color="brand" />
          <span className="sui-ml-2"><Typography variant="body-lg" weight="bold">{prompt}</Typography></span>
        </div>
      )}
      <Typography>
        {descriptionText || description}
      </Typography>
      {showMulchOptions && (
        <MulchOptions
          channel={channel}
          size={calculate.bagSize}
          mulch={calculate.mulchType}
          handleBagSize={handleBagSize}
          handleMulchType={handleMulchType}
        />
      )}
      <div className="sui-flex sui-mt-3 sui-mb-3">
        <div className="sui-pr-5">
          <FormController>
            <span className="sui-pt-1"><FormLabel>Calculate by:</FormLabel></span>
            <RadioGroup>
              <SelectionControlLabel value="Length x Width" label="Length x Width">
                <Radio
                  onChange={handleToggleText}
                  checked={calculate.calcByLengthWidth}
                />
              </SelectionControlLabel>
              <SelectionControlLabel value="Square Footage" label="Square Footage">
                <Radio
                  onChange={handleToggleText}
                  checked={calculate.calcByArea}
                />
              </SelectionControlLabel>
            </RadioGroup>
          </FormController>
        </div>
        {lineItemName === 'Area' && !hideMeasuringTips && (
          <CalculatorModal />
        )}
      </div>

      {calculate.calcByLengthWidth
        && calculate.areas.map((element, elementIndex) => {
          errorBox.widthError = false;
          errorBox.heightLengthError = false;
          if (calculate.hasError) {
            errorBox.widthError = element.width === '';
            errorBox.heightLengthError = element.length === '';
          }
          return (
            <CalculatorInputElement
              index={elementIndex}
              key={elementIndex}
              errorBox={{ ...errorBox }}
              areas={areas}
              step={step}
              calculatorType={calculatorType}
              lineItemName={lineItemName}
              dimensionNames={dimensionNames}
              handleInputChange={handleInputChange}
              removeInputElement={removeInputElement}
              showDescriptions={showDescriptions}
              hideAreaText={hideAreaText}
              inputType="lengthWidth"
            />
          );
        })}
      {calculate.calcByArea
        && calculate.areas.map((element, elementIndex) => {
          errorBox.areaError = false;
          if (calculate.hasError) {
            errorBox.areaError = element.value === '';
          }
          return (
            <CalculatorInputElement
              index={elementIndex}
              key={elementIndex}
              errorBox={{ ...errorBox }}
              areas={areas}
              step={step}
              calculatorType={calculatorType}
              lineItemName={lineItemName}
              dimensionNames={dimensionNames}
              handleInputChange={handleInputChange}
              removeInputElement={removeInputElement}
              showDescriptions={showDescriptions}
              hideAreaText={hideAreaText}
              inputType="area"
            />
          );
        })}
      {calculate.step < maxAreaNumber
          && (
            <div className="add-area">
              <Button variant="text" onClick={addElement}>{`+ Add ${lineItemName}`}</Button>
            </div>
          )}
      {calculate.hasError
          && (
            <Alert status="error">Please enter all required fields.</Alert>
          )}
      <div className="sui-mt-4">
        <Button
          variant="primary"
          onClick={() => bindCalcTotal({ isClick: true })}
        >
          Calculate
        </Button>
      </div>
      {showMulchOptions && (
        <div className="sui-mt-5">
          <Typography variant="body-xs" color="subtle">
            {description}
          </Typography>
        </div>
      )}
    </div>
  );
};

Calculator.displayName = 'Calculator';

Calculator.dataModel = {
  clientOnlyProduct: alias('product').params({
    itemId: stringType().isRequired()
  }).shape({
    dataSources: stringType(),
    info: shapeType({
      productDepartment: stringType(),
      classNumber: stringType(),
      subClassNumber: stringType()
    }),
  })
};

Calculator.propTypes = {
  unitsPerCase: oneOfType([string, number]),
  calculatorType: string,
  maxAreaNumber: number,
  lineItemName: string,
  dimensionNames: arrayOf(string),
  productCategory: string,
  addAdditional: bool,
  onCalculate: func,
  hideHeader: bool,
  hideMeasuringTips: bool,
  showMulchOptions: bool,
  showDescriptions: bool,
  hideAreaText: bool,
  descriptionText: string,
  itemId: string
};
Calculator.defaultProps = {
  unitsPerCase: '0',
  calculatorType: '',
  lineItemName: 'Area',
  dimensionNames: ['Width', 'Height'],
  productCategory: '',
  addAdditional: false,
  maxAreaNumber: 3,
  onCalculate: () => { },
  hideHeader: false,
  hideMeasuringTips: false,
  showMulchOptions: false,
  showDescriptions: false,
  hideAreaText: false,
  descriptionText: '',
  itemId: null
};
export { Calculator };
